/**
 * Apply a unified diff to an original text string.
 * Parses standard unified diff format with @@ hunks.
 * Returns the patched text.
 * Throws UnifiedDiffError if a hunk cannot be applied.
 */

export class UnifiedDiffError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "UnifiedDiffError"
  }
}

interface Hunk {
  oldStart: number
  oldCount: number
  newStart: number
  newCount: number
  lines: HunkLine[]
}

interface HunkLine {
  type: "context" | "add" | "remove"
  content: string
}

function parseHunkHeader(
  line: string
): { oldStart: number; oldCount: number; newStart: number; newCount: number } | null {
  // Match: @@ -oldStart,oldCount +newStart,newCount @@
  // Or:    @@ -oldStart +newStart @@
  const match = line.match(/^@@\s+-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s+@@/)
  if (!match) return null
  return {
    oldStart: parseInt(match[1], 10),
    oldCount: match[2] ? parseInt(match[2], 10) : 1,
    newStart: parseInt(match[3], 10),
    newCount: match[4] ? parseInt(match[4], 10) : 1,
  }
}

function parseHunks(diffText: string): Hunk[] {
  const lines = diffText.split("\n")
  const hunks: Hunk[] = []
  let currentHunk: Hunk | null = null

  for (const line of lines) {
    const header = parseHunkHeader(line)
    if (header) {
      if (currentHunk) {
        hunks.push(currentHunk)
      }
      currentHunk = { ...header, lines: [] }
      continue
    }

    if (!currentHunk) continue

    if (line.startsWith("-")) {
      currentHunk.lines.push({ type: "remove", content: line.substring(1) })
    } else if (line.startsWith("+")) {
      currentHunk.lines.push({ type: "add", content: line.substring(1) })
    } else if (line.startsWith(" ")) {
      currentHunk.lines.push({ type: "context", content: line.substring(1) })
    } else if (line === "\\ No newline at end of file") {
      // Skip this marker
      continue
    } else if (line !== "") {
      // Treat non-empty lines without prefix as context
      currentHunk.lines.push({ type: "context", content: line })
    }
  }

  if (currentHunk) {
    hunks.push(currentHunk)
  }

  return hunks
}

function getExpectedOldLines(hunk: Hunk): string[] {
  return hunk.lines.filter((l) => l.type === "context" || l.type === "remove").map((l) => l.content)
}

function matchesAt(originalLines: string[], position: number, expectedLines: string[]): boolean {
  if (position < 0 || position + expectedLines.length > originalLines.length) {
    return false
  }
  for (let i = 0; i < expectedLines.length; i++) {
    if (originalLines[position + i] !== expectedLines[i]) {
      return false
    }
  }
  return true
}

function findHunkPosition(originalLines: string[], hunk: Hunk): number {
  const expectedLines = getExpectedOldLines(hunk)
  if (expectedLines.length === 0) {
    // Add-only hunk (oldCount=0): oldStart refers to the line after which to insert
    return hunk.oldStart
  }

  const nominalPosition = hunk.oldStart - 1

  // Try exact position first
  if (matchesAt(originalLines, nominalPosition, expectedLines)) {
    return nominalPosition
  }

  // Fuzzy: try offsets -1, +1, -2, +2, -3, +3
  for (let offset = 1; offset <= 3; offset++) {
    if (matchesAt(originalLines, nominalPosition - offset, expectedLines)) {
      return nominalPosition - offset
    }
    if (matchesAt(originalLines, nominalPosition + offset, expectedLines)) {
      return nominalPosition + offset
    }
  }

  throw new UnifiedDiffError(
    `Hunk at line ${hunk.oldStart} does not match the original text (tried fuzzy ±3 lines)`
  )
}

export function applyUnifiedDiff(originalText: string, diffText: string): string {
  const hunks = parseHunks(diffText)

  if (hunks.length === 0) {
    return originalText
  }

  const trailingNewline = originalText.endsWith("\n")
  const originalLines = originalText.split("\n")
  // If the text ends with \n, split produces an extra empty string at the end — remove it
  if (trailingNewline && originalLines[originalLines.length - 1] === "") {
    originalLines.pop()
  }

  // Sort hunks by oldStart to process in order
  const sortedHunks = [...hunks].sort((a, b) => a.oldStart - b.oldStart)

  const result: string[] = []
  let originalIndex = 0

  for (const hunk of sortedHunks) {
    const position = findHunkPosition(originalLines, hunk)

    // Copy unchanged lines before this hunk
    while (originalIndex < position) {
      result.push(originalLines[originalIndex])
      originalIndex++
    }

    // Apply the hunk
    for (const line of hunk.lines) {
      if (line.type === "context") {
        result.push(line.content)
        originalIndex++
      } else if (line.type === "remove") {
        originalIndex++
      } else {
        result.push(line.content)
      }
    }
  }

  // Copy remaining lines after the last hunk
  while (originalIndex < originalLines.length) {
    result.push(originalLines[originalIndex])
    originalIndex++
  }

  const joined = result.join("\n")
  return trailingNewline ? joined + "\n" : joined
}
